import { motion } from 'framer-motion';
import { useEffect, useRef, CSSProperties, useState } from 'react';

const Hero = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Verificar se é desktop baseado na largura da janela
  useEffect(() => {
    const checkIfDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    
    // Verificar inicialmente
    checkIfDesktop();
    
    // Adicionar listener para redimensionamento
    window.addEventListener('resize', checkIfDesktop);
    
    // Limpeza ao desmontar
    return () => {
      window.removeEventListener('resize', checkIfDesktop);
    };
  }, []);

  // Efeito para monitorar o scroll e mostrar/esconder o botão de voltar ao topo
  useEffect(() => {
    const handleScroll = () => {
      // Mostrar o botão quando a página rolar mais de 300px
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Função para rolar até a próxima seção
  const scrollToNextSection = () => {
    const nextSection = document.getElementById('about');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Função para rolar até o topo
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const sectionStyle: CSSProperties = {
    position: 'relative',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundImage: 'url(/images/background.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };

  const overlayStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(23, 37, 84, 0.5)', // Ajustando opacidade do overlay
    zIndex: 1,
  };

  const containerStyle: CSSProperties = {
    width: '100%',
    maxWidth: '1280px',
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '0 1rem',
    zIndex: 10,
  };
  
  const textCenterStyle: CSSProperties = {
    textAlign: 'center',
    position: 'relative',
    width: '100%',
  };
  
  const logoContainerStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '2rem',
    width: '100%',
  };

  const logoImageStyle: CSSProperties = {
    objectFit: 'contain',
    maxWidth: '100%',
    height: 'auto',
    width: isDesktop ? '600px' : '100%',
    maxHeight: isDesktop ? '300px' : '200px',
  };

  const arrowContainerStyle: CSSProperties = {
    position: 'absolute',
    bottom: '2rem',
    left: '0',
    right: '0',
    width: '100%',
    margin: '0 auto',
    zIndex: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  };

  const scrollTopButtonStyle: CSSProperties = {
    position: 'fixed',
    bottom: '2rem',
    right: '2rem',
    backgroundColor: '#ffb03f',
    color: 'white',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
    cursor: 'pointer',
    zIndex: 100,
    transition: 'opacity 0.3s, transform 0.3s',
    opacity: showScrollTop ? 1 : 0,
    transform: showScrollTop ? 'translateY(0)' : 'translateY(20px)',
    pointerEvents: showScrollTop ? 'all' : 'none',
  };

  // Variantes de animação para a seta
  const arrowVariants = {
    initial: {
      y: 0,
    },
    animate: {
      y: [0, 10, 0],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        repeatType: "loop" as const,
        ease: "easeInOut",
      },
    },
  };

  return (
    <>
      <section style={sectionStyle}>
        <div style={overlayStyle}></div>
        
        <div style={containerStyle}>
          <motion.div
            style={textCenterStyle}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div style={logoContainerStyle}>
              <motion.img
                alt="Hackathon Happy Escolas 2025"
                style={logoImageStyle}
                src="/images/logo.png"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              />
            </div>
          </motion.div>
        </div>

        <motion.div 
          style={arrowContainerStyle}
          variants={arrowVariants}
          initial="initial"
          animate="animate"
          onClick={scrollToNextSection}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 5V19M12 19L5 12M12 19L19 12"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.div>
      </section>

      {/* Botão Back to Top */}
      <motion.div 
        style={scrollTopButtonStyle}
        onClick={scrollToTop}
        whileHover={{ backgroundColor: '#f59e0b' }}
        whileTap={{ scale: 0.95 }}
      >
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="M12 19V5M12 5L5 12M12 5L19 12" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </motion.div>
    </>
  );
};

export default Hero; 